import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { MetaTags } from '../../../../components/SEO/MetaTags';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
export function BlogPostPassiveAggressiveTextsFR() {
  const articleData = {
    title: 'Le Guide Ultime des Textes Passifs-Agressifs',
    description: 'Confus par des textes qui vous frustrent ou vous agacent ? Apprenez à repérer les messages passifs-agressifs avec la détection par IA de Toxic Detector.',
    slug: 'ultimate-guide-passive-aggressive-texts',
    publishDate: '2025-05-22',
    author: 'Rédaction Toxic Detector'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: "Qu'est-ce que les textes passifs-agressifs ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Des textes qui transmettent de l'hostilité, de la critique ou de la frustration indirectement, laissant souvent le destinataire confus ou agacé."
      }
    }, {
      '@type': 'Question',
      name: 'Pourquoi les gens envoient-ils des messages passifs-agressifs ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "C'est souvent un moyen caché d'exprimer la colère, de culpabiliser ou de manipuler sans confrontation ouverte."
      }
    }, {
      '@type': 'Question',
      name: "L'IA peut-elle détecter le comportement passif-agressif dans les messages ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Oui. Toxic Detector analyse le ton, la formulation, l'utilisation des emojis, les modèles de réponse et le sarcasme subtil pour signaler les textes manipulateurs."
      }
    }]
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: articleData.title,
    description: articleData.description,
    image: 'https://toxicdetector.com/images/logo.png',
    author: {
      '@type': 'Person',
      name: articleData.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector',
      logo: {
        '@type': 'ImageObject',
        url: 'https://toxicdetector.com/images/logo.png'
      }
    },
    datePublished: articleData.publishDate,
    url: `https://toxicdetector.com/fr/blog/${articleData.slug}`
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: 'https://toxicdetector.com/'
      });
    } catch (err) {
      navigator.clipboard.writeText('https://toxicdetector.com/');
      alert('Lien copié dans le presse-papiers !');
    }
  };
  return <>
      <MetaTags title="Le Guide Ultime des Textes Passifs-Agressifs — Comment l'IA détecte la manipulation | ToxicDetector" description="Confus par des textes qui vous frustrent ? Apprenez à repérer les messages passifs-agressifs avec l'IA." keywords="textes passifs-agressifs, comportement passif-agressif caché, modèles de manipulation de texte, détecteur d'agression passive IA" canonical={`https://toxicdetector.com/fr/blog/${articleData.slug}`} ogType="article" ogImage="https://cdn.magicpatterns.com/uploads/rgRp5kcuKrQdAWFQxfbcKt/file_0000000085487243beb3c848ebc5ef15.png" schema={[faqSchema, articleSchema]} data-id="element-5963" />
      <div className="flex flex-col w-full min-h-screen bg-[#fafaf9]" data-id="element-5964">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95" data-id="element-5965">
          <div className="max-w-4xl mx-auto px-6 py-5" data-id="element-5966">
            <div className="flex items-center justify-between" data-id="element-5967">
              <Link to="/" className="text-2xl font-bold tracking-tight" data-id="element-5968">
                🚩 ToxicDetector
              </Link>
              <button onClick={handleShare} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Partager l'article" data-id="element-5969">
                <Share2 size={20} data-id="element-5970" />
              </button>
            </div>
          </div>
        </header>

        <article className="max-w-4xl mx-auto w-full px-6 py-12" data-id="element-5971">
          <Link to="/" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors" data-id="element-5972">
            <ArrowLeft size={16} className="mr-2" data-id="element-5973" />
            Retour à l'Accueil
          </Link>

          <header className="mb-12" data-id="element-5974">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight" data-id="element-5975">
              {articleData.title}
            </h1>
            <div className="flex items-center text-gray-600 text-sm gap-3" data-id="element-5976">
              <time dateTime={articleData.publishDate} data-id="element-5977">
                {new Date(articleData.publishDate).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </time>
              <span className="text-gray-300" data-id="element-5978">•</span>
              <span data-id="element-5979">{articleData.author}</span>
            </div>
          </header>

          <div className="mb-12" data-id="element-5980">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sJh1uV1DahBAgXTJX4idSp/1763121685117.jpg" alt="IA analysant des SMS passifs-agressifs" className="w-full rounded-2xl shadow-lg" data-id="element-5981" />
          </div>

          <div className="bg-white rounded-2xl p-7 border border-gray-200 mb-12" data-id="element-5982">
            <h2 className="font-bold text-lg mb-5" data-id="element-5983">Table des Matières</h2>
            <ol className="space-y-3 text-sm list-decimal list-inside" data-id="element-5984">
              <li data-id="element-5985">
                <a href="#sure-whatever" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-5986">
                  Le classique "Bien sûr, peu importe"
                </a>
              </li>
              <li data-id="element-5987">
                <a href="#backhanded-compliment" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-5988">
                  Le compliment ambigu
                </a>
              </li>
              <li data-id="element-5989">
                <a href="#friendly-reminder" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-5990">
                  Le "rappel amical" qui est en fait une pique
                </a>
              </li>
              <li data-id="element-5991">
                <a href="#ghostly-reply" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-5992">
                  La réponse fantôme
                </a>
              </li>
              <li data-id="element-5993">
                <a href="#sarcasm-humor" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-5994">
                  Sarcasme déguisé en humour
                </a>
              </li>
              <li data-id="element-5995">
                <a href="#conditional-niceness" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-5996">
                  Gentillesse conditionnelle
                </a>
              </li>
              <li data-id="element-5997">
                <a href="#rollercoaster" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-5998">
                  Les montagnes russes aller-retour
                </a>
              </li>
            </ol>
          </div>

          <div className="prose prose-lg prose-gray max-w-none" data-id="element-5999">
            <p className="text-xl mb-8 leading-relaxed text-gray-700" data-id="element-6000">
              Avez-vous déjà reçu un SMS qui vous a fait vous demander si vous
              réagissiez de manière excessive – même s'il vous a laissé agacé,
              frustré ou confus ? C'est ça, les{' '}
              <strong data-id="element-6001">textes passifs-aggressifs</strong> en action.
            </p>
            <p className="mb-12 text-lg leading-relaxed text-gray-600" data-id="element-6002">
              Notre <strong data-id="element-6003">détection IA</strong> peut repérer ces modèles de{' '}
              <strong data-id="element-6004">comportement manipulateur</strong> instantanément. Voici
              votre guide ultime.
            </p>

            <section id="sure-whatever" className="mb-12" data-id="element-6005">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6006">
                1️⃣ Le classique "Bien sûr, peu importe"
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-6007">
                <p className="italic text-gray-700 text-lg" data-id="element-6008">
                  "Bien sûr, tout ce qui te rend heureux..."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-6009">
                À première vue, cela semble agréable – mais en dessous ? Rejet
                ou sarcasme. L'IA signale ces phrases.
              </p>
            </section>

            <section id="backhanded-compliment" className="mb-12" data-id="element-6010">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6011">
                2️⃣ Le compliment ambigu
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-6012">
                <p className="italic text-gray-700 text-lg" data-id="element-6013">
                  "Wow, tu as vraiment fait ça... tant mieux pour toi."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-6014">
                Ça a l'air gentil ? Pas vraiment. Des piques cachées comme
                celles-ci sapent votre confiance.
              </p>
            </section>

            <section id="friendly-reminder" className="mb-12" data-id="element-6015">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6016">
                3️⃣ Le "rappel amical" qui est en fait une pique
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-6017">
                <p className="italic text-gray-700 text-lg" data-id="element-6018">
                  "Juste pour que tu saches... j'ai nettoyé toute la cuisine."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-6019">
                Les textes passifs-agressifs masquent souvent la critique sous
                forme d'information utile.
              </p>
            </section>

            <section id="ghostly-reply" className="mb-12" data-id="element-6020">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6021">
                4️⃣ La réponse fantôme
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-6022">
                <p className="italic text-gray-700 text-lg" data-id="element-6023">
                  "K..." ou pas de réponse du tout
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-6024">
                Les réponses courtes, retardées ou manquantes peuvent punir sans
                confrontation.
              </p>
            </section>

            <section id="sarcasm-humor" className="mb-12" data-id="element-6025">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6026">
                5️⃣ Sarcasme déguisé en humour
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-6027">
                <p className="italic text-gray-700 text-lg" data-id="element-6028">
                  "Oh, tu es tellement intelligent 😏"
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-6029">
                Le sarcasme caché est un outil clé. Notre IA capte le contexte.
              </p>
            </section>

            <section id="conditional-niceness" className="mb-12" data-id="element-6030">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6031">
                6️⃣ Gentillesse conditionnelle
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-6032">
                <p className="italic text-gray-700 text-lg" data-id="element-6033">
                  "J'aiderais... si je n'avais rien de prévu."
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-6034">
                Les passifs-agressifs fixent des limites pour culpabiliser.
              </p>
            </section>

            <section id="rollercoaster" className="mb-12" data-id="element-6035">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6036">
                7️⃣ Les montagnes russes aller-retour
              </h2>
              <div className="bg-gray-50 border-l-4 border-gray-900 p-6 mb-6 rounded-r-xl" data-id="element-6037">
                <p className="italic text-gray-700 text-lg" data-id="element-6038">
                  Hauts et bas extrêmes dans les textes
                </p>
              </div>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-6039">
                Le ton fluctuant est un signal d'alarme de comportement
                manipulateur.
              </p>
            </section>

            <section className="mb-12" data-id="element-6040">
              <h2 className="text-3xl font-bold mb-6 tracking-tight" data-id="element-6041">
                🛡 Comment vous protéger
              </h2>
              <ul className="space-y-4 text-lg text-gray-600" data-id="element-6042">
                <li className="flex items-start gap-3" data-id="element-6043">
                  <span className="text-gray-900 font-bold" data-id="element-6044">•</span>
                  <span data-id="element-6045">Faites confiance à votre instinct.</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-6046">
                  <span className="text-gray-900 font-bold" data-id="element-6047">•</span>
                  <span data-id="element-6048">Gardez des captures d'écran.</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-6049">
                  <span className="text-gray-900 font-bold" data-id="element-6050">•</span>
                  <span data-id="element-6051">Évitez de suranalyser les textes individuels.</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-6052">
                  <span className="text-gray-900 font-bold" data-id="element-6053">•</span>
                  <span data-id="element-6054">Utilisez Toxic Detector.</span>
                </li>
              </ul>
            </section>

            <section className="mb-16" data-id="element-6055">
              <h2 className="text-3xl font-bold mb-8 tracking-tight" data-id="element-6056">
                Questions Fréquentes
              </h2>
              <div className="space-y-6" data-id="element-6057">
                {faqSchema.mainEntity.map((faq, index) => <div key={index} className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-6058">
                    <h3 className="font-bold text-lg mb-3 text-gray-900" data-id="element-6059">
                      {faq.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-6060">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>)}
              </div>
            </section>
          </div>

          <div className="bg-gray-900 rounded-2xl p-12 text-center text-white mt-16" data-id="element-6061">
            <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-6062">
              💀 Arrêtez de décoder. Commencez à savoir.
            </h2>
            <p className="text-gray-300 mb-8 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-6063">
              Arrêtez de deviner. Téléchargez vos textes sur Toxic Detector et
              obtenez un score de toxicité instantané.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20" data-id="element-6064">
              <p className="font-bold text-xl" data-id="element-6065">
                "🚩 65% agression passive détectée — comportement dédaigneux
                répété trouvé !"
              </p>
            </div>
            <Link to="/upload" className="inline-block bg-white text-gray-900 px-10 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors" data-id="element-6066">
              Scannez vos chats maintenant →
            </Link>
          </div>

          <RelatedBlogPosts posts={[{
          title: 'Votre ex est-il un narcissique caché ?',
          description: 'Découvrez si votre ex montre des signes de comportement narcissique caché dans les textes.',
          slug: 'is-your-ex-a-covert-narcissist',
          emoji: '🎭'
        }, {
          title: 'Apprenez les termes : Narcissisme & Gaslighting',
          description: 'Comprenez les schémas de narcissisme et de gaslighting dans les relations.',
          slug: 'narcissism-gaslighting-explained',
          emoji: '💀'
        }]} data-id="element-6067" />

          <div className="mt-12 text-center" data-id="element-6068">
            <button onClick={handleShare} className="inline-flex items-center text-gray-700 hover:text-gray-900 font-medium transition-colors" data-id="element-6069">
              <Share2 size={18} className="mr-2" data-id="element-6070" />
              Partager cet article
            </button>
          </div>
        </article>

        <footer className="bg-gray-900 text-white py-12 mt-16" data-id="element-6071">
          <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-6072">
            <p className="text-gray-400 text-sm" data-id="element-6073">
              © {new Date().getFullYear()} ToxicDetector. Tous droits réservés.
            </p>
          </div>
        </footer>
      </div>
    </>;
}